import { isInactive } from './is-inactive';
import { type DatePreset, startDateInWindow } from './start-date-window';

export const CATALOGUE_VIEWS = ['upcoming', 'past'] as const;

export type CatalogueView = (typeof CATALOGUE_VIEWS)[number];

export type CatalogueUniverses = {
  types: readonly string[];
  attendance: readonly string[];
  access: readonly string[];
  languages: readonly string[];
};

export type CatalogueRecord = {
  wallStart: number;
  endAt?: number;
  wallDay?: string;
  month?: string;
  theme: string;
  type: string[];
  attendance: string[];
  access: string;
  language: string[];
  cityKeys: string[];
};

export type CatalogueSelection = {
  theme: string;
  types: string[];
  attendance: string[];
  access: string[];
  languages: string[];
  cities: string[];
  datePreset: DatePreset;
  dateDay: string;
};

export const inCatalogueView = (
  record: CatalogueRecord,
  view: CatalogueView,
  now: number,
): boolean => {
  if (record.endAt == null) {
    return false;
  }

  const upcoming = record.endAt >= now;
  return view === 'upcoming' ? upcoming : !upcoming;
};

export const matchesCatalogue = (
  record: CatalogueRecord,
  selection: CatalogueSelection,
  universes: CatalogueUniverses,
): boolean => {
  const access = record.access === '' ? [] : [record.access];

  return (
    (selection.theme === 'all' || record.theme === selection.theme) &&
    matchesSome(record.type, selection.types, universes.types) &&
    matchesSome(record.attendance, selection.attendance, universes.attendance) &&
    matchesSome(access, selection.access, universes.access) &&
    matchesSome(record.language, selection.languages, universes.languages) &&
    matchesCities(record.cityKeys, selection.cities) &&
    startDateInWindow(record.wallDay, selection.datePreset, selection.dateDay)
  );
};

export const compareCatalogueStart = (
  a: CatalogueRecord,
  b: CatalogueRecord,
  view: CatalogueView,
): number => {
  return view === 'upcoming' ? a.wallStart - b.wallStart : b.wallStart - a.wallStart;
};

const matchesSome = (
  values: string[],
  selected: string[],
  all: readonly string[],
): boolean => {
  if (isInactive(selected, all)) {
    return true;
  }

  return values.length > 0 && values.some((value) => selected.includes(value));
};

const matchesCities = (cityKeys: string[], selected: string[]): boolean => {
  if (selected.length === 0) {
    return true;
  }

  return cityKeys.length > 0 && cityKeys.some((key) => selected.includes(key));
};
